import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  ShoppingCart, 
  Landmark, 
  Heart, 
  GraduationCap, 
  PlaneTakeoff,
  MessageSquare,
  BarChart,
  Calendar,
  Globe,
  MessagesSquare,
  ArrowRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { animations } from '@/lib/animations';
import { CALENDLY_URL, ROUTES } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';

interface Solution {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

const Solutions = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].solutions;
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const solutionDetailsRef = useRef<HTMLDivElement>(null);

  const handleSolutionClick = (solution: Solution) => {
    setSelectedSolution(solution);
    setTimeout(() => {
      solutionDetailsRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const industries: Solution[] = [
    {
      icon: <ShoppingCart className="w-12 h-12 text-primary" />,
      title: t.industries.items.ecommerce.title,
      description: t.industries.items.ecommerce.description,
      benefits: t.industries.items.ecommerce.benefits
    },
    {
      icon: <Landmark className="w-12 h-12 text-primary" />,
      title: t.industries.items.financial.title,
      description: t.industries.items.financial.description,
      benefits: t.industries.items.financial.benefits
    },
    {
      icon: <Heart className="w-12 h-12 text-primary" />,
      title: t.industries.items.healthcare.title,
      description: t.industries.items.healthcare.description,
      benefits: t.industries.items.healthcare.benefits
    },
    {
      icon: <GraduationCap className="w-12 h-12 text-primary" />,
      title: t.industries.items.education.title,
      description: t.industries.items.education.description,
      benefits: t.industries.items.education.benefits
    },
    {
      icon: <PlaneTakeoff className="w-12 h-12 text-primary" />,
      title: t.industries.items.tourism.title,
      description: t.industries.items.tourism.description,
      benefits: t.industries.items.tourism.benefits
    }
  ];

  const features: Solution[] = [
    {
      icon: <MessageSquare className="w-12 h-12 text-primary" />,
      title: t.features.items.customerService.title,
      description: t.features.items.customerService.description,
      benefits: t.features.items.customerService.benefits
    },
    {
      icon: <BarChart className="w-12 h-12 text-primary" />,
      title: t.features.items.sentiment.title,
      description: t.features.items.sentiment.description,
      benefits: t.features.items.sentiment.benefits
    },
    {
      icon: <Calendar className="w-12 h-12 text-primary" />,
      title: t.features.items.reservations.title,
      description: t.features.items.reservations.description,
      benefits: t.features.items.reservations.benefits
    },
    {
      icon: <Globe className="w-12 h-12 text-primary" />,
      title: t.features.items.multilingual.title,
      description: t.features.items.multilingual.description,
      benefits: t.features.items.multilingual.benefits
    }
  ];

  const integrations: Solution[] = [
    {
      icon: <MessagesSquare className="w-12 h-12 text-primary" />,
      title: t.integrations.items[0].title,
      description: t.integrations.items[0].description,
      benefits: t.integrations.items[0].benefits
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${animations.smoothScroll}`}>
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className={`text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/50 bg-clip-text text-transparent ${animations.fadeInSlideUp}`}>
            {t.industries.title}
          </h1>
          <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.industries.subtitle}
          </p>
        </div>
      </section>

      {/* Solutions Tabs */}
      <section className={`container mx-auto px-4 py-16 ${animations.fadeIn} ${animations.delay300}`}>
        <Tabs defaultValue="industries" className="max-w-6xl mx-auto">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="industries">{t.tabs.byIndustry}</TabsTrigger>
            <TabsTrigger value="features">{t.tabs.byFeature}</TabsTrigger>
            <TabsTrigger value="integrations">{t.tabs.integrations}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="industries" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((solution, index) => (
                <Card 
                  key={index}
                  className={`p-6 cursor-pointer ${animations.cardHover} ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleSolutionClick(solution)}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`${animations.float}`}>
                      {solution.icon}
                    </div>
                    <h3 className="text-xl font-semibold mt-4 mb-2">{solution.title}</h3>
                    <p className="text-muted-foreground">{solution.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="features" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((solution, index) => (
                <Card 
                  key={index}
                  className={`p-6 cursor-pointer ${animations.cardHover} ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleSolutionClick(solution)}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`${animations.float}`}>
                      {solution.icon}
                    </div>
                    <h3 className="text-xl font-semibold mt-4 mb-2">{solution.title}</h3>
                    <p className="text-muted-foreground">{solution.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="integrations" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {integrations.map((solution, index) => (
                <Card 
                  key={index}
                  className={`p-6 cursor-pointer ${animations.cardHover} ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => handleSolutionClick(solution)}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`${animations.float}`}>
                      {solution.icon}
                    </div>
                    <h3 className="text-xl font-semibold mt-4 mb-2">{solution.title}</h3>
                    <p className="text-muted-foreground">{solution.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Selected Solution Details */}
      {selectedSolution && (
        <section 
          ref={solutionDetailsRef}
          className={`container mx-auto px-4 py-16 bg-muted/30 ${animations.fadeIn}`}
        >
          <div className="max-w-4xl mx-auto">
            <div className={`flex items-center gap-4 mb-8 ${animations.popIn}`}>
              <div className={`${animations.float}`}>
                {selectedSolution.icon}
              </div>
              <h2 className="text-3xl font-bold">{selectedSolution.title}</h2>
            </div>
            <p className={`text-xl mb-8 ${animations.slideRight} ${animations.delay100}`}>
              {selectedSolution.description}
            </p>
            <h3 className={`text-2xl font-semibold mb-4 ${animations.slideRight} ${animations.delay200}`}>
              {t.selectedSolution.benefits}
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedSolution.benefits.map((benefit, index) => (
                <li 
                  key={index} 
                  className={`flex items-center gap-2 ${animations.slideRight} ${animations.cardHover}`}
                  style={{ animationDelay: `${(index * 100) + 300}ms` }}
                >
                  <ArrowRight className={`w-4 h-4 text-primary ${animations.wiggle}`} />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <div className={`mt-8 flex justify-center ${animations.fadeInSlideUp} ${animations.delay500}`}>
              <Button size="lg" className={`gap-2 ${animations.buttonHover}`}>
                {t.selectedSolution.explore} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className={`container mx-auto px-4 py-16 ${animations.fadeInSlideUp}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">{t.cta.title}</h2>
          <p className={`text-xl text-muted-foreground mb-8 ${animations.fadeIn} ${animations.delay100}`}>
            {t.cta.description}
          </p>
          <Button 
            size="lg" 
            className={`gap-2 ${animations.buttonHover} ${animations.delay200}`} 
            onClick={() => navigate(ROUTES.CONTACT_SALES)}
          >
            {t.cta.button} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Solutions; 